/**
 * Thin fetch wrapper for the app's API. Same-origin cookies are sent
 * automatically since the SPA and the API share the Pages origin.
 *
 * `getJson` / `sendJson` are intentionally low-level: they always resolve
 * (no throw on HTTP errors) and return `{status, body}` so callers can
 * branch on status codes. The `api` object groups the typed wrappers per
 * endpoint.
 */
import type {
  Achievement,
  AlphabetLetter,
  PerSessionStickerTally,
  Session,
  SessionInput,
  Settings,
  SettingsInput,
  StreakState,
  UnlockedSticker,
  Week,
  WeekUpsertInput,
} from "@/types/data";

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

export interface ApiResponse<T> {
  status: number;
  body: T | null;
}

async function readBody<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function getJson<T>(path: string): Promise<ApiResponse<T>> {
  const res = await fetch(path, { credentials: "same-origin" });
  return { status: res.status, body: await readBody<T>(res) };
}

async function sendJson<T>(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  payload?: unknown,
): Promise<ApiResponse<T>> {
  const init: RequestInit = {
    method,
    credentials: "same-origin",
    headers: JSON_HEADERS,
  };
  if (payload !== undefined) {
    init.body = JSON.stringify(payload);
  }
  const res = await fetch(path, init);
  return { status: res.status, body: await readBody<T>(res) };
}

// ─── Response shapes ───────────────────────────────────────────────────────

export interface SessionResponse {
  authenticated: boolean;
}

export interface ApiError {
  error: string;
  detail?: string;
}

export interface WeeksListResponse {
  current: Week | null;
  archived: Week[];
}

export interface SessionsListResponse {
  sessions: Session[];
  limit: number;
  offset: number;
}

export interface AchievementsResponse {
  achievements: Achievement[];
  perSessionTallies: PerSessionStickerTally[];
  alphabet: AlphabetLetter[];
}

/** New shape for POST /api/sessions — wraps the inserted session with
 *  the list of stickers that just unlocked for the celebration screen. */
export interface CreateSessionResponse {
  session: Session;
  newlyUnlocked: UnlockedSticker[];
}

export interface MarkSeenInput {
  oneTimeIds?: string[];
  perSessionRowIds?: number[];
  /** Bare letters (e.g. ["a","b"]) to clear the "new!" flag on. */
  alphabetLetters?: string[];
}

/** Wire shape for GET /api/dashboard. See functions/lib/routes/dashboard.ts
 *  for what each number means. */
export interface DashboardResponse {
  daysPlayed: number;
  currentStreak: number;
  longestStreak: number;
  averageFirstPassScore: number | null;
  totalWordsMastered: number;
  averageTimeSpentSeconds: number | null;
  mostRecentTimeSpentSeconds: number | null;
  frequentMisses: Array<{ spelling: string; missCount: number }>;
  recentSessions: Array<{
    id: number;
    startedAt: string;
    weekId: number;
    weekLabel: string | null;
    firstPassScore: number | null;
    totalWords: number;
    missedWords: string[];
    countsTowardProgress: boolean;
  }>;
}

// ─── Public surface ────────────────────────────────────────────────────────

export const api = {
  // Auth
  getSession: () => getJson<SessionResponse>("/api/session"),
  login: (password: string) =>
    sendJson<{ ok: true } | ApiError>("POST", "/api/login", { password }),
  logout: () => sendJson<{ ok: true }>("POST", "/api/logout"),

  // Weeks
  listWeeks: () => getJson<WeeksListResponse>("/api/weeks"),
  createWeek: (input: WeekUpsertInput) => sendJson<Week | ApiError>("POST", "/api/weeks", input),
  updateWeek: (id: number, input: WeekUpsertInput) =>
    sendJson<Week | ApiError>("PUT", `/api/weeks/${id}`, input),
  deleteWeek: (id: number) => sendJson<{ ok: true } | ApiError>("DELETE", `/api/weeks/${id}`),

  // Sessions
  listSessions: (params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set("limit", String(params.limit));
    if (params?.offset !== undefined) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return getJson<SessionsListResponse>(`/api/sessions${query ? `?${query}` : ""}`);
  },
  createSession: (input: SessionInput) =>
    sendJson<CreateSessionResponse | ApiError>("POST", "/api/sessions", input),

  // Streak / achievements / settings
  getStreak: () => getJson<StreakState>("/api/streak"),
  setSkipThisWeek: (enabled: boolean) =>
    sendJson<StreakState | ApiError>("PUT", "/api/streak/skip-this-week", {
      enabled,
    }),
  listAchievements: () => getJson<AchievementsResponse>("/api/achievements"),
  markAchievementsSeen: (input: MarkSeenInput) =>
    sendJson<{ ok: true } | ApiError>("POST", "/api/achievements/seen", input),
  getDashboard: () => getJson<DashboardResponse>("/api/dashboard"),

  // Admin (destructive)
  resetStats: () =>
    sendJson<{ ok: true } | ApiError>("POST", "/api/admin/reset-stats"),
  clearWords: () =>
    sendJson<{ ok: true } | ApiError>("POST", "/api/admin/clear-words"),
  getSettings: () => getJson<Settings>("/api/settings"),
  updateSettings: (input: SettingsInput) =>
    sendJson<Settings | ApiError>("PUT", "/api/settings", input),
};
